import { jsx as _jsx } from "react/jsx-runtime";
import { Component, createContext, lazy, Suspense, useContext, useMemo, } from 'react';
import { usePlatformBootstrap } from '../runtime/platform-bootstrap-provider.js';
const PlatformWebHostRegistryContext = createContext(null);
function conditionMatches(contribution, values) {
    const condition = contribution.settingCondition;
    if (condition === null)
        return true;
    const configured = values[condition.key];
    const actual = condition.property === null
        ? configured
        : typeof configured === 'object' && configured !== null && !Array.isArray(configured)
            ? configured[condition.property]
            : undefined;
    return Object.is(actual, condition.equals);
}
export function resolvePlatformWebHostContributions(contributions, access) {
    const availableModules = new Set(access.modules.filter(({ available }) => available).map(({ id }) => id));
    return contributions
        .filter((contribution) => availableModules.has(contribution.moduleId))
        .filter((contribution) => (contribution.requiredPermission === null ||
        access.permissions.includes(contribution.requiredPermission)))
        .filter((contribution) => conditionMatches(contribution, access.configurationValues))
        .sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id));
}
export function PlatformWebHostRegistryProvider(props) {
    const bootstrap = usePlatformBootstrap();
    const registered = useMemo(() => props.moduleContributions
        .flatMap(({ hostContributions }) => hostContributions)
        .map((contribution) => ({ ...contribution, Component: lazy(contribution.load) })), [props.moduleContributions]);
    const resolved = useMemo(() => {
        return resolvePlatformWebHostContributions(registered, {
            modules: bootstrap.modules,
            permissions: bootstrap.permissions,
            configurationValues: bootstrap.configuration.values,
        });
    }, [bootstrap.configuration.values, bootstrap.modules, bootstrap.permissions, registered]);
    return (_jsx(PlatformWebHostRegistryContext, { value: resolved, children: props.children }));
}
class PlatformWebHostErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() {
        return { failed: true };
    }
    componentDidCatch(_error, information) {
        console.error(`[CREST] Web host contribution failed: ${this.props.contribution.moduleId}:${this.props.contribution.id}`, information.componentStack);
    }
    render() {
        if (!this.state.failed)
            return this.props.children;
        if (this.props.contribution.errorBehavior === 'collapse')
            return null;
        return (_jsx("section", { className: "crest-card", "data-contribution-id": this.props.contribution.id, "data-module-id": this.props.contribution.moduleId, role: "alert", children: this.props.failureMessage }));
    }
}
export function usePlatformWebHostContributions(slot, contractVersion) {
    const registry = useContext(PlatformWebHostRegistryContext);
    if (registry === null)
        throw new Error('PlatformWebHostRegistryProvider is missing.');
    return useMemo(() => registry.filter((contribution) => (contribution.slot === slot && contribution.contractVersion === contractVersion)), [contractVersion, registry, slot]);
}
export function usePlatformWebHostLauncherContributions(contractVersion) {
    const registry = useContext(PlatformWebHostRegistryContext);
    if (registry === null)
        throw new Error('PlatformWebHostRegistryProvider is missing.');
    return useMemo(() => registry.filter((contribution) => (contribution.launcher?.contractVersion === contractVersion)), [contractVersion, registry]);
}
export function platformWebHostContributionAnchorId(contributionId) {
    return `crest-host-contribution-${contributionId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}
export function PlatformWebHostSlot(props) {
    const contributions = usePlatformWebHostContributions(props.slot, props.contractVersion);
    if (contributions.length === 0)
        return props.emptyFallback ?? null;
    return (_jsx("div", { className: "crest-stack", "data-host-slot": props.slot, children: contributions.map((contribution) => (_jsx("div", { "data-contribution-id": contribution.id, "data-module-id": contribution.moduleId, id: platformWebHostContributionAnchorId(contribution.id), children: _jsx(PlatformWebHostErrorBoundary, { contribution: contribution, failureMessage: props.failureMessage, children: _jsx(Suspense, { fallback: _jsx("p", { className: "crest-muted", children: props.loadingMessage }), children: _jsx(contribution.Component, { hostContext: props.hostContext }) }) }) }, contribution.id))) }));
}
//# sourceMappingURL=platform-web-host-slot.js.map